/* eslint-disable */
import { SUPABASE_URL, SUPABASE_ANON_KEY } from './security/supabaseClient';

async function sendEmail(type, payload, token) {
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/send-email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token || SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ type, ...payload }),
    });
    if (!res.ok) { console.warn(`[Email] '${type}' failed:`, await res.text()); return false; }
    console.log(`[Email] Sent: ${type} → ${payload.to}`);
    return true;
  } catch (err) {
    console.warn(`[Email] Error '${type}':`, err.message);
    return false;
  }
}

// ── Retreats ─────────────────────────────────────────────────────────────────
// A retreat is not a class and must not borrow the class template. Someone who
// has just paid for a 23-day residential course needs the things that make the
// trip happen: both dates, what is and is not included, what to bring, how to
// reach the venue, the cancellation terms, and any balance still owed.
//
// The template for `retreat_booking_confirmed` lives in the send-email Edge
// Function, which is not in this repo — see docs/RETREAT_EMAIL_TEMPLATE.md for
// the markup to add there. Until it exists this call is a no-op, which is no
// worse than the nothing that was sent before.
export async function sendRetreatBookingConfirmation({
  studentEmail, studentName,
  retreatTitle, schoolName, schoolEmail, schoolPhone,
  startDate, endDate, durationDays, guests,
  location, venueAddress,
  includes, excludes, whatToBring, accommodationNote,
  totalAmount, currency, amountPaid, balanceDue, balanceDueDate,
  cancellationPolicy, bookingRef, paymentId,
  token,
}) {
  return sendEmail("retreat_booking_confirmed", {
    to: studentEmail,
    studentName, retreatTitle, schoolName, schoolEmail, schoolPhone,
    startDate, endDate, durationDays, guests,
    location, venueAddress,
    includes:   includes   || [],
    excludes:   excludes   || [],
    whatToBring: whatToBring || null,
    accommodationNote: accommodationNote || null,
    totalAmount, currency: currency || "USD",
    amountPaid:     amountPaid     ?? null,
    balanceDue:     balanceDue     ?? null,
    balanceDueDate: balanceDueDate || null,
    cancellationPolicy: cancellationPolicy || null,
    bookingRef, paymentId: paymentId || null,
  }, token);
}

// The school needs to know someone is coming — and when, and with how many.
export async function sendRetreatBookingAlert({
  schoolEmail, schoolName, retreatTitle,
  studentName, studentEmail, studentPhone,
  startDate, endDate, guests, totalAmount, currency, bookingRef, specialRequests,
  token,
}) {
  return sendEmail("retreat_booking_school_alert", {
    to: schoolEmail,
    schoolName, retreatTitle, studentName, studentEmail,
    studentPhone: studentPhone || null,
    startDate, endDate, guests,
    totalAmount, currency: currency || "USD",
    bookingRef,
    specialRequests: specialRequests || null,
  }, token);
}

export async function sendBookingConfirmation({
  studentEmail, studentName, classTitle, schoolName,
  bookingDate, bookingTime, duration, bookingRef,
  paymentId, totalAmount, isFree,
  commissionRate, schoolPayout, transferId, payoutSchedule,
  token,
}) {
  return sendEmail("booking_confirmed", {
    to: studentEmail,
    studentName, classTitle, schoolName,
    bookingDate, bookingTime, duration, bookingRef,
    paymentId:      paymentId || null,
    totalAmount:    totalAmount || "FREE",
    isFree:         !!isFree,
  }, token);
}

export async function sendNewBookingAlert({
  schoolEmail, schoolName, studentName, studentEmail,
  classTitle, bookingDate, bookingTime,
  totalAmount, schoolPayout, commission,
  bookingRef,
  // New: commission tier details for the school
  commissionRate, schoolTier, payoutSchedule, transferId,
  token,
}) {
  return sendEmail("new_booking_alert", {
    to:           schoolEmail,
    schoolName,   studentName,    studentEmail,
    classTitle,   bookingDate,    bookingTime,
    totalAmount,
    schoolPayout,
    commission,
    bookingRef,
    // Payout details — shown in school notification email
    commissionRate:  commissionRate  || 15,
    schoolTier:      schoolTier      || "starter",
    payoutSchedule:  payoutSchedule  || "per_booking",
    transferId:      transferId      || null,
  }, token);
}

export async function sendAccommodationRequest({ schoolEmail, schoolName, studentName, studentEmail, roomType, checkIn, checkOut, nights, totalAmount, schoolPayout, bookingRef, token }) {
  return sendEmail("accommodation_request", { to: schoolEmail, schoolName, studentName, studentEmail, roomType, checkIn, checkOut, nights, totalAmount, schoolPayout, bookingRef }, token);
}

export async function sendStayConfirmed({ studentEmail, studentName, schoolName, roomType, checkIn, checkOut, nights, bookingRef, token }) {
  return sendEmail("stay_confirmed", { to: studentEmail, studentName, schoolName, roomType, checkIn, checkOut, nights, bookingRef }, token);
}

export async function sendStayDeclined({ studentEmail, studentName, schoolName, roomType, checkIn, bookingRef, reason, token }) {
  return sendEmail("stay_declined", { to: studentEmail, studentName, schoolName, roomType, checkIn, bookingRef, reason: reason || "The school is unable to accommodate your request." }, token);
}

export async function sendWelcomeStudent({ studentEmail, studentName, token }) {
  return sendEmail("welcome_student", { to: studentEmail, studentName }, token);
}

export async function sendWelcomeSchool({ schoolEmail, schoolName, token }) {
  return sendEmail("welcome_school", { to: schoolEmail, schoolName }, token);
}

export async function sendWelcomeTeacher({ teacherEmail, teacherName, token }) {
  return sendEmail("welcome_teacher", { to: teacherEmail, teacherName }, token);
}

// ── Welcome dispatch ─────────────────────────────────────────────────────────
// Rows for a new account are created in four different places (the signup
// handlers, the post-confirmation recovery, and the role picker), because
// email confirmation means the account and its role row are created at
// different moments. Calling the individual senders from each of those would
// be four chances to wire one up wrongly or forget it, which is how the
// welcomes came to exist for a year without ever being sent. One entry point
// instead: call this immediately after the role row is written.
//
// Never throws and never blocks. A welcome email that fails must not take the
// signup down with it — the account is already real by this point.
export async function sendRoleWelcome({ role, email, name, schoolName, schoolCity, schoolCountry, token }) {
  if (!email) return false;
  try {
    if (role === "school") {
      const n = schoolName || name;
      // Fire both, but do not let the admin alert's failure hide the welcome's.
      const [welcomed] = await Promise.all([
        sendWelcomeSchool({ schoolEmail: email, schoolName: n, token }),
        sendAdminNewSchoolAlert({ schoolName: n, schoolEmail: email, schoolCity, schoolCountry, token }),
      ]);
      return welcomed;
    }
    if (role === "teacher") return await sendWelcomeTeacher({ teacherEmail: email, teacherName: name, token });
    if (role === "student") return await sendWelcomeStudent({ studentEmail: email, studentName: name, token });
    return false;
  } catch (err) {
    console.warn("[Email] welcome dispatch failed:", err?.message);
    return false;
  }
}

export async function sendAdminNewSchoolAlert({ schoolName, schoolEmail, schoolCity, schoolCountry, token }) {
  return sendEmail("admin_new_school", { to: "admin@yogastay.app", schoolName, schoolEmail, schoolCity, schoolCountry }, token);
}

// Admin approval: the school is public from now on, without the badge.
export async function sendSchoolListed({ schoolEmail, schoolName, missing, token }) {
  return sendEmail("school_listed", { to: schoolEmail, schoolName, missing }, token);
}

export async function sendSchoolApproved({ schoolEmail, schoolName, token }) {
  return sendEmail("school_approved", { to: schoolEmail, schoolName }, token);
}

export async function sendSchoolRejected({ schoolEmail, schoolName, reason, token }) {
  return sendEmail("school_rejected", { to: schoolEmail, schoolName, reason }, token);
}

// ── Losing the badge ─────────────────────────────────────────────────────────
// Admin moderation wrote a reason to the database from the day it was built
// and the drawer told the admin it was "sent to the school". It never was —
// nothing in the admin console sent any email at all. These three close that.
export async function sendSchoolUnverified({ schoolEmail, schoolName, reason, token }) {
  return sendEmail("school_unverified", { to: schoolEmail, schoolName, reason }, token);
}

// "Request documents" from the admin console: the existing
// school_profile_incomplete template with the items this school still lacks.
export async function sendSchoolProfileIncomplete({ schoolEmail, schoolName, missing, token }) {
  return sendEmail("school_profile_incomplete", { to: schoolEmail, schoolName, missing }, token);
}

export async function sendSchoolSuspended({ schoolEmail, schoolName, reason, token }) {
  return sendEmail("school_suspended", { to: schoolEmail, schoolName, reason }, token);
}

// ── Verification lifecycle ───────────────────────────────────────────────────
// A school that chooses to stop must NOT get school_unverified. That template
// says "fix what is noted above and reapply", which reads as an accusation to
// someone who simply decided not to renew. Different event, different email.
export async function sendVerificationCancelled({ schoolEmail, schoolName, activeUntil, token }) {
  return sendEmail("school_verification_cancelled", {
    to: schoolEmail, schoolName,
    activeUntil: activeUntil || null,   // null = free period, no end date to quote
  }, token);
}

export async function sendVerificationLapsed({ schoolEmail, schoolName, expiredOn, reason, token }) {
  return sendEmail("school_verification_lapsed", {
    to: schoolEmail, schoolName, expiredOn,
    reason: reason || "lapsed",         // 'lapsed' adds the expired-card line
  }, token);
}
// ─── BOOKING CANCELLED → Student ──────────────────────────────────────────────
export async function sendBookingCancelled({
  studentEmail, studentName, classTitle, schoolName,
  bookingDate, bookingRef, refundAmount, refundPercent, token
}) {
  return sendEmail("booking_cancelled", {
    to: studentEmail,
    studentName, classTitle, schoolName,
    bookingDate, bookingRef,
    refundAmount: refundAmount || "N/A",
    refundPercent: refundPercent || 0,
  }, token);
}

// ─── BOOKING CANCELLED ALERT → School ─────────────────────────────────────────
export async function sendCancellationAlertToSchool({
  schoolEmail, schoolName, studentName, studentEmail,
  classTitle, bookingDate, bookingRef, refundPercent, token
}) {
  return sendEmail("booking_cancelled_school", {
    to: schoolEmail,
    schoolName, studentName, studentEmail,
    classTitle, bookingDate, bookingRef,
    refundPercent: refundPercent || 0,
  }, token);
}